
import React, { useRef } from 'react';
import { WeatherData, WeatherCondition } from '@/types/weather';
import { Card, CardContent } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import WeatherBackground from '@/components/weather/WeatherBackground';
import WeatherEffects from '@/components/weather/WeatherEffects';
import WeatherIcon from '@/components/weather/WeatherIcon';
import WeatherQuoteDisplay from '@/components/weather/WeatherQuoteDisplay';
import WeatherDetails from '@/components/weather/WeatherDetails';
import ShareButtons from '@/components/weather/ShareButtons';
import ForecastDayCard from '@/components/weather/ForecastDayCard';
import { getRandomTemperature, getRandomHumidity, getRandomWindSpeed } from '@/components/weather/WeatherUtils';
import { getRandomQuoteForCondition } from '@/data/weatherQuotes';
import { addDays } from 'date-fns';
import { useIsMobile } from '@/hooks/use-mobile';

interface WeatherImageDisplayProps {
  weatherData: WeatherData;
}

// Function to get related weather conditions for a forecast
const getRelatedWeatherConditions = (currentCondition: WeatherCondition): WeatherCondition[] => {
  // Map of related conditions for each weather type
  const relatedConditionsMap: Record<WeatherCondition, WeatherCondition[]> = {
    'clear': ['partlyCloudy', 'cloudy'],
    'partlyCloudy': ['clear', 'cloudy', 'rain'],
    'cloudy': ['partlyCloudy', 'rain', 'fog'],
    'rain': ['cloudy', 'heavyRain', 'partlyCloudy'],
    'heavyRain': ['rain', 'thunderstorm', 'cloudy'],
    'thunderstorm': ['heavyRain', 'rain', 'cloudy'],
    'snow': ['fog', 'cloudy', 'clear'],
    'fog': ['cloudy', 'snow', 'partlyCloudy'],
    'wind': ['partlyCloudy', 'cloudy', 'clear'],
    'alert': ['thunderstorm', 'heavyRain', 'wind']
  };
  
  return relatedConditionsMap[currentCondition] || ['cloudy', 'partlyCloudy'];
};

// Function to get a random weather condition from the related ones
const getRandomRelatedWeatherCondition = (current: WeatherCondition): WeatherCondition => {
  const relatedConditions = getRelatedWeatherConditions(current);
  return relatedConditions[Math.floor(Math.random() * relatedConditions.length)];
};

const WeatherImageDisplay: React.FC<WeatherImageDisplayProps> = ({ weatherData }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const isMultiDay = weatherData.forecastDuration && weatherData.forecastDuration > 1;

  // Generate or use the provided quotes - ensure we always have a quote
  const [displayQuote, setDisplayQuote] = React.useState(
    weatherData.customQuote || weatherData.quote || getRandomQuoteForCondition(weatherData.condition)
  );

  // Update the quote when the weather data changes
  React.useEffect(() => {
    // If custom quote is provided, use it
    if (weatherData.customQuote) {
      setDisplayQuote(weatherData.customQuote);
    } 
    // Otherwise use the existing quote or get a random one for the condition
    else {
      setDisplayQuote(weatherData.quote || getRandomQuoteForCondition(weatherData.condition));
    }
  }, [weatherData.customQuote, weatherData.quote, weatherData.condition]);

  // Generate random values if not provided
  const temperature = weatherData.temperature || getRandomTemperature(weatherData.condition);
  const humidity = weatherData.humidity || getRandomHumidity(weatherData.condition);
  const windSpeed = weatherData.windSpeed || getRandomWindSpeed(weatherData.condition);
  
  // Generate forecast days data if needed, with related weather conditions
  const forecastDays = React.useMemo(() => {
    if (!isMultiDay || !weatherData.forecastDuration) return [];
    
    const days = [];
    for (let i = 1; i < weatherData.forecastDuration; i++) {
      days.push({
        date: addDays(weatherData.date, i),
        condition: getRandomRelatedWeatherCondition(weatherData.condition)
      });
    }
    return days;
  }, [isMultiDay, weatherData.date, weatherData.condition, weatherData.forecastDuration]);

  // Dynamic aspect ratio based on forecast days and device
  // Aumentato il rapporto per dispositivi mobili per garantire più spazio verticale
  const aspectRatio = isMultiDay 
    ? (isMobile ? 1/2 : 4/5)  // Modificato da 2/3 a 1/2 per mobile con forecast multipli
    : (isMobile ? 3/5 : 1);   // Modificato da 1 a 3/5 per mobile con singolo forecast

  return (
    <div className="flex flex-col gap-4">
      <Card className="overflow-hidden border-0 shadow-xl bg-transparent" ref={cardRef}>
        <CardContent className="p-0 relative">
          {/* Aumentata l'altezza minima per mobile per evitare il taglio */}
          <div className={`${isMobile ? (isMultiDay ? 'min-h-[1500px]' : 'min-h-[1500px]') : ''}`}>
            <AspectRatio ratio={aspectRatio} className="w-full">
              <div className="w-full h-full relative overflow-hidden">
                {/* Background Image */}
                <WeatherBackground 
                  location={weatherData.location} 
                  condition={weatherData.condition} 
                />
                
                {/* Weather Effects (Rain, Snow, etc.) */}
                <WeatherEffects condition={weatherData.condition} />
                
                <div className="absolute inset-0 p-6 md:p-8 text-white flex flex-col justify-between z-10">
                  {/* Weather Details (Location, Date, Temperature) */}
                  <div>
                    <WeatherDetails 
                      temperature={temperature}
                      humidity={humidity}
                      windSpeed={windSpeed}
                      location={weatherData.location}
                      date={weatherData.date}
                      condition={weatherData.condition}
                    />
                  </div>
                  
                  {/* Weather Icon */}
                  <div className={`flex justify-center items-center ${
                    isMultiDay 
                      ? 'my-4 flex-grow-0' 
                      : (isMobile ? 'my-8 flex-grow-0' : 'my-2 flex-grow')
                  }`}>
                    <WeatherIcon condition={weatherData.condition} />
                  </div>
                  
                  {/* Bottom content: Quote and forecast */}
                  <div className="mt-auto">
                    {/* Weather Quote - Always show a quote */}
                    <WeatherQuoteDisplay 
                      quote={displayQuote} 
                      isMultiDay={isMultiDay} 
                    />

                    {/* Multi-day forecast */}
                    {isMultiDay && forecastDays.length > 0 && (
                      <div className="mt-4">
                        <h3 className="text-sm font-medium mb-2 text-center text-shadow-md">{weatherData.forecastDuration}-day forecast</h3>
                        <div className="grid grid-cols-2 gap-2">
                          {/* First day (current) */}
                          <ForecastDayCard 
                            date={weatherData.date} 
                            condition={weatherData.condition} 
                          />
                          
                          {/* Additional forecast days */}
                          {forecastDays.map((day, index) => (
                            <ForecastDayCard 
                              key={index}
                              date={day.date}
                              condition={day.condition}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Disclaimer at the bottom - more visible now */}
                    <div className="mt-4 bg-black/60 p-2 text-xs text-center rounded-md">
                      <p className="font-medium">⚠️ DISCLAIMER ⚠️</p>
                      <p>For entertainment purposes only. Not for real forecasts. Not responsible for usage.</p>
                    </div>
                  </div>
                </div>
              </div>
            </AspectRatio>
          </div>
        </CardContent>
      </Card>
      
      {/* Share Buttons */}
      <ShareButtons weatherData={weatherData} imageRef={cardRef} />
    </div>
  );
};

export default WeatherImageDisplay;
